import Image from "next/image";
import { IoIosArrowForward } from "react-icons/io";
import { motion, Variants } from "framer-motion";
import { useLanguage } from "@/components/context/LanguageContext";
import Link from "next/link";
import { MdArrowForwardIos } from "react-icons/md";
import { TbUserSquareRounded } from "react-icons/tb";
import useSWR from "swr";
import type { News } from "@/types/news";
import { limitText } from "@/utils/limitText";
import Head from "next/head";


const cardVariant: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.42, 0, 0.58, 1],
        },
    },
};


const containerVariant = {
    hidden: {
        opacity: 0
    },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2, // Jarak antar anak saat muncul
            delayChildren: 0.1,   // Tunda animasi anak setelah parent mulai
        },
    },
};

const itemVariants: Variants = {
    hidden: {
        opacity: 0,
        y: 30
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 15,
            duration: 0.5,
        },
    },
};

const NewsAndArtikel = () => {
    const fetcher = (url: string) => fetch(url).then((res) => res.json());
    const { data, error, isLoading } = useSWR("/api/news", fetcher);
    const { language } = useLanguage();
    const news: News[] = data?.news || [];

    return (
        <>
            <Head>
                <title>News & Article</title>
                <meta name="description" content="Generated by create next app" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
            </Head>

            <div className="my-22">
                <div className="relative w-full h-[300px]">
                    <Image
                        src="/gambar2.jpg"
                        alt="Group Photo"
                        fill
                        className=" object-cover object-center"
                    />
                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 space-y-6 px-4 bg-gradient-to-r bg-transparent from-white/85 via-[#f0fdf4]/65 to-white/1 w-[75%] h-[300px] p-4 ">
                        <div className="space-y-20 xl:ml-15 items-center justify-center xl:justify-start">
                            <div className="ml-15 flex items-center space-x-2">
                                <p className="text-sm text-gray-800">Home</p>
                                <IoIosArrowForward className="text-gray-800 text-xl" />
                                <p className="text-sm text-gray-800">Profile</p>
                                <IoIosArrowForward className="text-gray-800 text-xl" />
                                <p className="text-sm text-gray-800">News & Article</p>
                            </div>
                            <motion.div className=""
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5 }}>
                                <p className="text-3xl xl:text-4xl font-bold  bg-clip-text bg-gradient-to-r text-gray-700 ml-15">
                                    {language === "en" ? "News & Article" : "Berita & Artikel"}
                                </p>
                            </motion.div>
                        </div>
                    </div>
                </div>
                <div className="my-10">
                    {/* Cards */}
                    <motion.div
                        variants={containerVariant}
                        initial="hidden"
                        animate="visible"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.5 }}
                        className="max-w-screen-xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                    >
                        {news.map((item, index) => (
                            <motion.div
                                key={item.id}
                                variants={itemVariants}
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                                className="max-w-sm mx-auto rounded overflow-hidden shadow-lg bg-white"
                            >
                                <div>
                                    <Image
                                        src={item.headerImage}
                                        alt={item.title.en}
                                        width={600}
                                        height={250}
                                        className="object-cover w-full h-[250px]"
                                    />
                                </div>
                                <div className="px-6 py-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <div className="flex items-center">
                                            <TbUserSquareRounded className="text-xl text-[#005b96]" />
                                            <p className="font-light text-xs ml-2 text-gray-600">{item.author}</p>
                                        </div>
                                        <p className="font-light text-xs text-gray-600">
                                            <time dateTime={item.date}>{item.date}</time>
                                        </p>
                                    </div>
                                    <div className="font-bold text-xl mb-2">
                                        {language === "en" ? item.title.en : item.title.id}
                                    </div>
                                    {item.title.en.length <= 58 ? <br /> : ''}
                                </div>
                                <div className="mt-1 flex items-center justify-between p-4 bg-gray-100">
                                    <Link href={`/news-and-artikel/${item.id}`} className="text-[#005b96] text-md font-medium">
                                        Read More
                                    </Link>
                                    <MdArrowForwardIos className="text-[#005b96] text-lg" />
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>

                </div>
            </div>
        </>
    );
}

export default NewsAndArtikel;